import React, { useState, useEffect, useMemo, useRef } from 'react';
import { DataStore } from '@aws-amplify/datastore';
import { generateClient } from 'aws-amplify/api';
import { Progress, Roadmap } from '../models';
import RoadmapEditor from './RoadmapEditor';
import {
  CheckCircle2,
  Circle,
  Clock,
  CalendarRange,
  Download,
  TrendingUp,
  Target,
  BookOpen,
  Edit,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// GraphQL query to fetch a roadmap by ID
const GET_ROADMAP = `
  query GetRoadmap($id: ID!) {
    getRoadmap(id: $id) {
      id
      items {
        id
        title
        duration
        month
        startMonth
        endMonth
        cost
        description
        type
        coveredInBootcamp
      }
      certifications {
        id
        name
        cost
        duration
      }
    }
  }
`;

function groupByMonth(roadmap, maxMonth) {
  if (!roadmap) return [];
  const total = Number(roadmap.totalMonths) || 0;
  const limit = Math.min(total || 0, Number(maxMonth) || total);
  const items = Array.isArray(roadmap.items) ? roadmap.items : [];

  const months = Array.from({ length: limit }, (_, idx) => idx + 1).map((m) => ({
    month: m,
    items: items.filter((i) => (i?.startMonth ?? i?.month ?? 0) <= m && (i?.endMonth ?? i?.month ?? 0) >= m),
  }));
  return months;
}

async function exportElementToPdf({ element, fileName }) {
  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      backgroundColor: '#020617',
      useCORS: true,
      logging: false,
      allowTaint: true,
    });

    const imgData = canvas.toDataURL('image/png', 1.0);
    const pdf = new jsPDF('p', 'pt', 'a4');

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 28;
    const usableWidth = pageWidth - margin * 2;
    const imgWidth = usableWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let y = margin;
    let remainingHeight = imgHeight;

    pdf.addImage(imgData, 'PNG', margin, y, imgWidth, imgHeight, undefined, 'FAST');
    remainingHeight -= (pageHeight - margin * 2);

    let page = 1;
    while (remainingHeight > 0) {
      page += 1;
      pdf.addPage();
      const offsetY = -(pageHeight - margin * 2) * (page - 1) + margin;
      pdf.addImage(imgData, 'PNG', margin, offsetY, imgWidth, imgHeight, undefined, 'FAST');
      remainingHeight -= (pageHeight - margin * 2);
    }

    pdf.save(fileName);
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
}

export default function RoadmapTracker({ roadmapRun, isPro, onBack }) {
  const containerRef = useRef(null);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [roadmap, setRoadmap] = useState(null);

  // Load roadmap data from RoadmapRun
  useEffect(() => {
    const loadRoadmap = async () => {
      if (!roadmapRun) return;

      try {
        let roadmapData = null;

        // Check if roadmap is already loaded (eager loaded)
        if (roadmapRun.roadmap && roadmapRun.roadmap.items) {
          roadmapData = roadmapRun.roadmap;
        } else if (roadmapRun.roadmap && roadmapRun.roadmap.id) {
          // Try DataStore first with timeout
          try {
            roadmapData = await Promise.race([
              DataStore.query(Roadmap, roadmapRun.roadmap.id),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('DataStore timeout')), 3000)
              )
            ]);
          } catch (dsError) {
            console.warn('DataStore query failed, trying GraphQL:', dsError.message);

            // Fallback to GraphQL API
            try {
              const client = generateClient();
              const response = await client.graphql({
                query: GET_ROADMAP,
                variables: { id: roadmapRun.roadmap.id },
                authMode: 'userPool'
              });
              roadmapData = response.data?.getRoadmap;
            } catch (gqlError) {
              console.error('GraphQL fallback also failed:', gqlError);
            }
          }
        }

        if (roadmapData) {
          setRoadmap({
            items: roadmapData.items || [],
            certifications: roadmapData.certifications || [],
            totalMonths: roadmapRun.totalMonths || 6,
          });
        } else {
          // If no roadmap data found, set empty to stop loading spinner
          console.warn('No roadmap data found for run:', roadmapRun.id);
          setRoadmap({
            items: [],
            certifications: [],
            totalMonths: roadmapRun.totalMonths || 6,
          });
        }
      } catch (error) {
        console.error('Error loading roadmap:', error);
        // Set empty roadmap to stop infinite loading
        setRoadmap({
          items: [],
          certifications: [],
          totalMonths: roadmapRun?.totalMonths || 6,
        });
      }
    };

    loadRoadmap();
  }, [roadmapRun]);

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      setLoading(true);
      // Add timeout to prevent hanging
      const savedProgress = await Promise.race([
        DataStore.query(Progress).then(rows => rows.filter(p => !p.id.includes('::project::'))),
        new Promise((resolve) =>
          setTimeout(() => resolve([]), 3000) // Return empty array on timeout
        )
      ]);
      if (savedProgress.length > 0 && savedProgress[0]?.data) {
        const data = typeof savedProgress[0].data === 'string'
          ? JSON.parse(savedProgress[0].data)
          : savedProgress[0].data;
        setProgress(data);
      } else {
        // Initialize progress structure
        const initialProgress = {
          items: {},
          certifications: {},
        };
        setProgress(initialProgress);
      }
    } catch (error) {
      console.error('Error loading progress:', error);
      setProgress({ items: {}, certifications: {} });
    } finally {
      setLoading(false);
    }
  };

  const saveProgress = async (newProgress) => {
    try {
      const existing = await DataStore.query(Progress).then(rows => rows.filter(p => !p.id.includes('::project::')));
      if (existing.length > 0) {
        await DataStore.save(
          Progress.copyOf(existing[0], (updated) => {
            updated.data = JSON.stringify(newProgress);
          })
        );
      } else {
        await DataStore.save(
          new Progress({
            data: JSON.stringify(newProgress),
          })
        );
      }
      setProgress(newProgress);
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const toggleItemComplete = (itemId, itemTitle) => {
    if (!isPro) return;
    const newProgress = {
      ...progress,
      items: {
        ...progress.items,
        [itemId]: !progress.items?.[itemId],
      },
    };
    saveProgress(newProgress);
  };

  const toggleCertComplete = (certId, certName) => {
    if (!isPro) return;
    const newProgress = {
      ...progress,
      certifications: {
        ...progress.certifications,
        [certId]: !progress.certifications?.[certId],
      },
    };
    saveProgress(newProgress);
  };

  const months = useMemo(() => groupByMonth(roadmap, roadmap?.totalMonths), [roadmap]);
  
  const allItems = useMemo(() => {
    if (!roadmap?.items) return [];
    return roadmap.items;
  }, [roadmap]);

  const completedItems = useMemo(() => {
    return Object.values(progress.items || {}).filter(Boolean).length;
  }, [progress]);

  const completedCerts = useMemo(() => {
    return Object.values(progress.certifications || {}).filter(Boolean).length;
  }, [progress]);

  const totalItems = allItems.length;
  const totalCerts = roadmap?.certifications?.length || 0;
  const overallProgress = totalItems + totalCerts > 0 
    ? ((completedItems + completedCerts) / (totalItems + totalCerts)) * 100 
    : 0;

  const onDownloadPdf = async () => {
    if (!containerRef.current) return;
    try {
      setIsExporting(true);
      await exportElementToPdf({ 
        element: containerRef.current, 
        fileName: `${roadmapRun?.title || 'roadmap'}-tracker.pdf` 
      });
    } catch (error) {
      console.error('PDF export failed:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  if (loading || !roadmap) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-slate-400">Loading roadmap...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            type="button"
            onClick={onBack}
            className="mb-4 text-sm font-bold text-slate-400 hover:text-white transition-colors"
          >
            ← Back to Portal
          </button>
          
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-extrabold bg-gradient-to-r from-green-400 to-green-300 bg-clip-text text-transparent mb-2">
                {roadmapRun?.title || 'Roadmap Tracker'}
              </h1>
              <p className="text-slate-400">{roadmapRun?.subtitle || ''}</p>
            </div>
            <div className="flex items-center gap-3">
              {isPro && (
                <RoadmapEditor
                  roadmapRun={roadmapRun}
                  isPro={isPro}
                  onSave={async (updatedRun) => {
                    // Reload roadmap data
                    if (updatedRun.roadmap && updatedRun.roadmap.id) {
                      const roadmapData = await DataStore.query(Roadmap, updatedRun.roadmap.id);
                      if (roadmapData) {
                        setRoadmap({
                          items: roadmapData.items || [],
                          certifications: roadmapData.certifications || [],
                          totalMonths: updatedRun.totalMonths || 6,
                        });
                      }
                    }
                  }}
                  onCancel={() => {}}
                />
              )}
              <button
                type="button"
                onClick={onDownloadPdf}
                disabled={isExporting}
                className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-950/40 px-4 py-2.5 text-sm font-bold text-slate-100 hover:bg-slate-950/70 transition-colors disabled:opacity-60"
              >
                <Download className="mr-2 h-4 w-4" />
                {isExporting ? 'Exporting…' : 'Export PDF'}
              </button>
            </div>
          </div>

          {/* Progress Stats */}
          {isPro && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/60 to-slate-900/40 p-6 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="rounded-xl bg-green-400/10 p-2 border border-green-400/20">
                    <Target className="h-5 w-5 text-green-400" />
                  </div>
                  <div className="text-2xl font-extrabold text-white">{overallProgress.toFixed(0)}%</div>
                </div>
                <div className="text-sm text-slate-400">Overall Progress</div>
                <div className="mt-3 h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-green-400 to-green-500 transition-all duration-500"
                    style={{ width: `${overallProgress}%` }}
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/60 to-slate-900/40 p-6 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="rounded-xl bg-blue-400/10 p-2 border border-blue-400/20">
                    <BookOpen className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="text-2xl font-extrabold text-white">
                    {completedItems}/{totalItems}
                  </div>
                </div>
                <div className="text-sm text-slate-400">Items Completed</div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/60 to-slate-900/40 p-6 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="rounded-xl bg-yellow-400/10 p-2 border border-yellow-400/20">
                    <TrendingUp className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="text-2xl font-extrabold text-white">
                    {completedCerts}/{totalCerts}
                  </div>
                </div>
                <div className="text-sm text-slate-400">Certifications</div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/60 to-slate-900/40 p-6 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="rounded-xl bg-purple-400/10 p-2 border border-purple-400/20">
                    <CalendarRange className="h-5 w-5 text-purple-400" />
                  </div>
                  <div className="text-2xl font-extrabold text-white">{roadmapRun?.totalMonths || 0}</div>
                </div>
                <div className="text-sm text-slate-400">Total Months</div>
              </div>
            </div>
          )}
        </div>

        {/* Roadmap Timeline */}
        <div
          ref={containerRef}
          className="rounded-3xl border border-slate-800 bg-gradient-to-b from-slate-950/40 to-slate-900/20 shadow-2xl overflow-hidden"
        >
          <div className="relative px-6 sm:px-8 py-8 bg-slate-900/10">
            <div className="absolute inset-0 pointer-events-none opacity-[0.08] [background-image:radial-gradient(#34d399_1px,transparent_1px)] [background-size:18px_18px]" />

            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-green-300/60 via-green-400/40 to-green-300/20" />

              <div className="space-y-12">
                {months.map(({ month, items }) => (
                  <div key={month} className="relative">
                    <div className="absolute left-1/2 -translate-x-1/2 -top-1">
                      <div className="h-3 w-3 rounded-full bg-green-300 shadow-[0_0_0_6px_rgba(34,197,94,0.12)]" />
                    </div>

                    <div className="flex justify-center">
                      <div className="inline-flex items-center gap-2 rounded-full border border-slate-800 bg-slate-950/60 px-4 py-2 text-sm font-extrabold text-green-300 shadow-lg">
                        <span>Month {month}</span>
                        <span className="text-[11px] font-bold text-slate-300">
                          {items.length} {items.length === 1 ? 'item' : 'items'}
                        </span>
                      </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                      {items.map((item, idx) => {
                        const isCert = item?.type === 'certification';
                        const isLeft = idx % 2 === 0;
                        const itemId = item?.id || `${month}-${idx}`;
                        const itemTitle = item?.title || item?.name || '';
                        const isCompleted = isCert 
                          ? progress.certifications?.[itemId] === true
                          : progress.items?.[itemId] === true;

                        return (
                          <div
                            key={itemId}
                            className={isLeft ? 'md:pr-6' : 'md:pl-6'}
                          >
                            <div
                              className={[
                                'rounded-2xl border bg-slate-950/35 p-5 shadow-xl transition-all',
                                'border-slate-800 hover:border-slate-700',
                                isCert ? 'ring-1 ring-green-400/30' : '',
                                isCompleted && isPro ? 'ring-2 ring-green-400/50 bg-green-400/5' : '',
                              ].join(' ')}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    {isPro && (
                                      <button
                                        type="button"
                                        onClick={() => isCert ? toggleCertComplete(itemId, itemTitle) : toggleItemComplete(itemId, itemTitle)}
                                        className="flex-shrink-0 mt-0.5"
                                        aria-label={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                                      >
                                        {isCompleted ? (
                                          <CheckCircle2 className="h-5 w-5 text-green-400" />
                                        ) : (
                                          <Circle className="h-5 w-5 text-slate-500 hover:text-green-400 transition-colors" />
                                        )}
                                      </button>
                                    )}
                                    <div className="text-lg font-extrabold text-white">
                                      {itemTitle}
                                    </div>
                                  </div>
                                  {item?.description && (
                                    <div className="mt-1 text-sm text-slate-300">
                                      {item.description}
                                    </div>
                                  )}
                                </div>
                                {isCert && (
                                  <div className="inline-flex items-center gap-1 rounded-full bg-green-400/10 border border-green-400/20 px-2.5 py-1 text-xs font-bold text-green-300">
                                    Certification
                                  </div>
                                )}
                              </div>

                              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-400">
                                {item?.duration && (
                                  <div className="inline-flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {item.duration} week{item.duration === 1 ? '' : 's'}
                                  </div>
                                )}
                                {isCompleted && isPro && (
                                  <div className="inline-flex items-center gap-1 text-green-400">
                                    <CheckCircle2 className="h-3 w-3" />
                                    Completed
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {!isPro && (
          <div className="mt-8 rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-6 text-center">
            <div className="text-lg font-bold text-yellow-300 mb-2">Sign in to track progress</div>
            <div className="text-sm text-slate-300">Mark items as complete, track your journey, and see your progress percentage.</div>
          </div>
        )}
      </div>
    </div>
  );
}
